import React, { useState, useEffect } from 'react';
import {
    Grid, Box, Typography, Button, TextField, Card, CardContent,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Divider, Chip
} from '@mui/material';
import Controls from '../../components/controls/Controls';
import { fetchData } from '../../services/ApiServices';
import { urlClientTariff, urlClientPackage } from '../../config';
import { logActionType } from '../../services/MyCollections';
import { logActivity } from '../../services/ApiServices';
import * as MyCollections from '../../services/MyCollections';
import _ from 'lodash';

export default function ClientTariffs({ token, userName, onNotify, onSuccess, canDeleteTariff = true, darkTheme }) {
    const [mode, setMode] = useState('edit'); // 'edit' | 'add' | 'delete'
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [formData, setFormData] = useState({});
    const [tariffs, setTariffs] = useState([]);
    const [packages, setPackages] = useState([]);
    const [errors, setErrors] = useState({});
    const [selectedTariff, setSelectedTariff] = useState(null);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [numberOfPackages, setNumberOfPackages] = useState(null);
    const regex = /^\d+$/;

    const initialClientTariffDTO = {
        id: null,
        description: '',
        packageDTOList: []
    };

    const [clientTariffDTO, setClientTariffDTO] = useState(initialClientTariffDTO);

    const notify = (msg, severity = 'success') => {
        if (onNotify) {
            onNotify(msg, severity);
        } else if (onSuccess) {
            onSuccess(msg);
        }
    };

    const fieldsToValidate = [
        'description',
        'tpackageTypeId',
        'packagePrice',
        'numberOfPackageDocuments',
        'documentPrice',
        'jobContractPrice',
        'mandateContractPrice',
        'appointedBoardMembersPrice',
        'balanceSheetOnRequest',
        'estonianCit',
        'premiumPersonnelPrice',
        'additionalConsultation',
        'additionalDeclaration',
        'correctionOfDeclaration',
        'otherServices',
        'sfToKrsAssistance',
        'finReportSmallClient',
        'finReportMediumClient',
        'finReportLargeClient'
    ];

    const mapToSelect = (item) => ({
        ...item,
        key: item.id,
        value: item.description,
    });

    const mapToSelect2 = (item) => ({
        ...item,
        key: item.id,
        value: item.packageDescription,
    });

    const loadTariffs = () => {
        fetchData(urlClientTariff + '/allForSelect', mapToSelect, (data) => {
            setTariffs(data.filter(t =>
                !t.discontinueDate &&
                Number(t.id) !== 61 &&
                Number(t.key) !== 61 &&
                t.description?.trim().toLowerCase() !== 'brak' &&
                t.value?.trim().toLowerCase() !== 'brak'
            ));
        }, console.error, token);
    };

    useEffect(() => {
        loadTariffs();
    }, []);

    const handleSwitchMode = (newMode) => {
        setMode(newMode);
        setSelectedTariff(null);
        setSelectedPackage(null);
        setNumberOfPackages(null);
        setFormData({});
        setErrors({});

        if (newMode === 'add') {
            fetchData(urlClientPackage + '/allForSelect', mapToSelect, (data) => {
                setPackages(data.filter(p =>
                    Number(p.id) !== 61 &&
                    Number(p.key) !== 61 &&
                    p.description?.trim().toLowerCase() !== 'brak' &&
                    p.value?.trim().toLowerCase() !== 'brak'
                ));
            }, console.error, token);
            setClientTariffDTO(initialClientTariffDTO);
        } else {
            loadTariffs();
        }
    };

    const handleClientTariffChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedTariff(selectedValue);
        if (selectedValue) {
            fetchData(urlClientPackage + `/?tariffId=${selectedValue}&isForClient=false`, mapToSelect2, setPackages, console.error, token);
        } else {
            setPackages([]);
        }
        setSelectedPackage(null);
        setFormData({});
    };

    const handleClientPackageChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedPackage(selectedValue);
        if (selectedValue && selectedTariff) {
            fetchLocal(urlClientPackage + `/package?tariffId=${selectedTariff}&packageTypeId=${selectedValue}`);
        } else {
            setFormData({});
        }
    };

    const fetchLocal = (url) => {
        fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(resp => resp.json())
            .then(data => setFormData(data))
            .catch(error => console.error(error));
    };

    const handleNumberOfPackages = (e) => {
        const selectedValue = parseInt(e.target.value, 10);
        setNumberOfPackages(selectedValue);

        const newPackageList = Array.from({ length: selectedValue }, () => ({
            id: null,
            description: '',
            packagePrice: null,
            numberOfPackageDocuments: null,
            documentPrice: null,
            jobContractPrice: null,
            mandateContractPrice: null,
            appointedBoardMembersPrice: null,
            balanceSheetOnRequest: null,
            estonianCit: null,
            premiumPersonnelPrice: null,
            additionalConsultation: null,
            additionalDeclaration: null,
            correctionOfDeclaration: null,
            otherServices: null,
            sfToKrsAssistance: null,
            finReportSmallClient: null,
            finReportMediumClient: null,
            finReportLargeClient: null,
            tpackageTypeId: null
        }));

        setClientTariffDTO(prevState => ({
            ...prevState,
            packageDTOList: newPackageList
        }));

        validateField(e.target.name, selectedValue);
    };

    const handleDescriptionChange = (e) => {
        const { value } = e.target;
        validateField("clientTariffDTO.description", value);
        setClientTariffDTO(prev => ({
            ...prev,
            description: value
        }));
    };

    const handlePackageFieldChange = (e, index) => {
        const { name, value } = e.target;

        if (name !== "description" && value !== "" && !regex.test(value)) {
            return;
        }
        validateField2(name, value, index);

        setClientTariffDTO(prevState => {
            const newPackages = [...prevState.packageDTOList];
            newPackages[index] = {
                ...newPackages[index],
                [name]: value
            };
            return {
                ...prevState,
                packageDTOList: newPackages
            };
        });
    };

    const localOnChange = (e, isFromTariff) => {
        const { name, value } = e.target;

        if (isFromTariff && name !== "description" && value !== "" && !regex.test(value)) {
            return;
        }
        validateField(name, value);
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const validateField = (name, value) => {
        const newErrors = { ...errors };
        if (value === null || value === '' || value === undefined) {
            newErrors[name] = 'Pole nie może być puste.';
        } else {
            delete newErrors[name];
        }
        setErrors(newErrors);
    };

    const validateField2 = (name, value, index) => {
        let newErrors = { ...errors };
        if (value === null || value === '' || value === undefined) {
            _.set(newErrors, `clientTariffDTO.packageDTOList[${index}].[${name}]`, "Pole nie może być puste.");
        } else {
            _.unset(newErrors, `clientTariffDTO.packageDTOList[${index}].[${name}]`);
        }
        setErrors(newErrors);
    };

    const validateOnSubmit = () => {
        let newErrors = {};
        let isValid = true;

        if (!clientTariffDTO.description) {
            _.set(newErrors, `clientTariffDTO.description`, "Pole nie może być puste.");
            isValid = false;
        }
        if (!numberOfPackages) {
            _.set(newErrors, `numberOfPackages`, "Pole nie może być puste.");
            isValid = false;
        }

        for (const field of fieldsToValidate) {
            for (let i = 0; i < clientTariffDTO.packageDTOList.length; i++) {
                const item = clientTariffDTO.packageDTOList[i];
                if (!item[field]) {
                    _.set(newErrors, `clientTariffDTO.packageDTOList[${i}].${field}`, "Pole nie może być puste.");
                    isValid = false;
                }
            }
        }
        setErrors(newErrors);
        return isValid;
    };

    const validateFormOnSubmit = () => {
        let formIsValid = true;
        let validationErrors = {};

        const requiredFields = [
            'description', 'packagePrice', 'numberOfPackageDocuments', 'documentPrice',
            'jobContractPrice', 'mandateContractPrice', 'appointedBoardMembersPrice',
            'balanceSheetOnRequest', 'estonianCit', 'premiumPersonnelPrice',
            'additionalConsultation', 'additionalDeclaration', 'correctionOfDeclaration',
            'otherServices', 'sfToKrsAssistance', 'finReportSmallClient',
            'finReportMediumClient', 'finReportLargeClient'
        ];

        requiredFields.forEach(field => {
            const val = formData?.[field];
            if (val === null || val === undefined || (typeof val === 'string' && val.trim() === '')) {
                validationErrors[field] = 'Pole jest wymagane';
                formIsValid = false;
            }
        });

        setErrors(validationErrors);
        return formIsValid;
    };

    const handleAddNewClientTariff = () => {
        let isValid = validateOnSubmit();
        let url = urlClientTariff + `/add`;

        if (isValid) {
            fetch(url, {
                method: "POST",
                body: JSON.stringify(clientTariffDTO),
                headers: {
                    'content-type': "application/json",
                    'Authorization': `Bearer ${token}`
                }
            })
                .then(resp => {
                    if (resp.status === 200) {
                        logActivity(logActionType.ADD_NEW_CLIENT_TARIFF + clientTariffDTO.description, userName, token);
                        notify('Cennik klienta został pomyślnie utworzony!', 'success');
                        setClientTariffDTO(initialClientTariffDTO);
                        setNumberOfPackages(null);
                        handleSwitchMode('edit');
                        return resp.json();
                    } else if (resp.status === 400) {
                        notify('Cennik o podanej nazwie już istnieje w bazie.', 'warning');
                    } else {
                        throw new Error(`HTTP error! Status: ${resp.status}`);
                    }
                })
                .catch(error => {
                    console.error('Błąd dodawania cennika:', error);
                    notify('Błąd podczas zapisywania cennika.', 'error');
                });
        } else {
            notify('Uzupełnij wszystkie wymagane pola formularza.', 'warning');
        }
    };

    const handleDeleteClientTariff = async () => {
        if (!selectedTariff) return;
        setOpenDeleteDialog(false);

        try {
            const resp = await fetch(urlClientTariff + `/delete/${selectedTariff}`, {
                method: "DELETE",
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (resp.status === 200) {
                const deletedName = tariffs.find(t => t.key === selectedTariff)?.value || '';
                logActivity("Usunięto cennik klienta: " + deletedName, userName, token);
                notify('Cennik został pomyślnie usunięty.', 'success');
                setSelectedTariff(null);
                loadTariffs();
            } else if (resp.status === 409) {
                const errText = await resp.text();
                notify(errText || 'Nie można usunąć cennika.', 'error');
            } else {
                notify('Wystąpił błąd podczas usuwania cennika.', 'error');
            }
        } catch (error) {
            console.error('Błąd usuwania cennika:', error);
            notify('Wystąpił błąd podczas usuwania cennika.', 'error');
        }
    };

    const handlePackageAmountsChange = () => {
        let isValid = validateFormOnSubmit();
        let url = urlClientPackage + `/edit`;

        if (isValid) {
            fetch(url, {
                method: "POST",
                body: JSON.stringify(formData),
                headers: {
                    'content-type': "application/json",
                    'Authorization': `Bearer ${token}`
                }
            })
                .then(resp => {
                    if (resp.status === 200) {
                        const clientTariff = tariffs.find(tariff => tariff.key === selectedTariff);
                        logActivity(logActionType.EDIT_CLIENT_TARIFF + "cennik: " + (clientTariff ? clientTariff.value : '') + ", pakiet: " + formData.description, userName, token);
                        notify('Stawki pakietu zostały pomyślnie zaktualizowane!', 'success');
                        if (selectedTariff) {
                            fetchData(urlClientPackage + `/?tariffId=${selectedTariff}&isForClient=false`, mapToSelect2, setPackages, console.error, token);
                        }
                        return resp.json();
                    } else if (resp.status === 400) {
                        notify('Pakiet o podanej nazwie już istnieje w bazie.', 'warning');
                    } else {
                        throw new Error(`HTTP error! Status: ${resp.status}`);
                    }
                })
                .then(data => {
                    if (data) {
                        setFormData(data);
                    }
                })
                .catch(error => {
                    console.error('Błąd edycji pakietu:', error);
                    notify('Błąd podczas zapisywania pakietu.', 'error');
                });
        }
    };

    // Helper komponent do renderowania spójnych sekcji pól pakietu
    const renderPackageSectionCards = ({
        isEditMode,
        packageIndex,
        data,
        onChangeHandler
    }) => {
        const getFieldVal = (fieldName) => {
            if (isEditMode) return data[fieldName] !== undefined ? data[fieldName] : '';
            return clientTariffDTO.packageDTOList[packageIndex]?.[fieldName] || '';
        };

        const getFieldError = (fieldName) => {
            if (isEditMode) return !!errors[fieldName];
            return !!(errors.clientTariffDTO?.packageDTOList?.[packageIndex]?.[fieldName]);
        };

        const getHelper = (fieldName) => {
            if (isEditMode) return errors[fieldName];
            return errors.clientTariffDTO?.packageDTOList?.[packageIndex]?.[fieldName];
        };

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Grupa 1: Podstawowe parametry */}
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#F8FAFC'
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkTheme ? '#93C5FD' : '#1D4ED8', mb: 1.5 }}>
                            <i className="fas fa-file-invoice" style={{ marginRight: 8 }}></i>
                            Podstawowe parametry pakietu
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={isEditMode ? 12 : 6}>
                                <TextField
                                    name="description"
                                    value={getFieldVal('description')}
                                    placeholder="np. Pakiet Standard"
                                    label="Nazwa pakietu"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('description')}
                                    helperText={getHelper('description')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            {!isEditMode && (
                                <Grid item xs={12} sm={6}>
                                    <Controls.Select
                                        name="tpackageTypeId"
                                        label="Typ pakietu"
                                        value={clientTariffDTO.packageDTOList[packageIndex]?.tpackageTypeId || ''}
                                        onChange={onChangeHandler}
                                        options={packages}
                                        error={getFieldError('tpackageTypeId')}
                                        size="small"
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    name="packagePrice"
                                    value={getFieldVal('packagePrice')}
                                    placeholder="np. 800"
                                    label="Cena bazowa pakietu (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('packagePrice')}
                                    helperText={getHelper('packagePrice')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    name="numberOfPackageDocuments"
                                    value={getFieldVal('numberOfPackageDocuments')}
                                    placeholder="np. 50"
                                    label="Liczba dokumentów w pakiecie"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('numberOfPackageDocuments')}
                                    helperText={getHelper('numberOfPackageDocuments')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    name="documentPrice"
                                    value={getFieldVal('documentPrice')}
                                    placeholder="np. 10"
                                    label="Stawka za dokument ponad limit (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('documentPrice')}
                                    helperText={getHelper('documentPrice')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Grupa 2: Kadry i płace */}
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#F8FAFC'
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkTheme ? '#86EFAC' : '#047857', mb: 1.5 }}>
                            <i className="fas fa-users" style={{ marginRight: 8 }}></i>
                            Obsługa kadrowo-płacowa
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    name="jobContractPrice"
                                    value={getFieldVal('jobContractPrice')}
                                    placeholder="0"
                                    label="Umowa o pracę (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('jobContractPrice')}
                                    helperText={getHelper('jobContractPrice')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    name="mandateContractPrice"
                                    value={getFieldVal('mandateContractPrice')}
                                    placeholder="0"
                                    label="Zlecenie / o dzieło (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('mandateContractPrice')}
                                    helperText={getHelper('mandateContractPrice')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    name="appointedBoardMembersPrice"
                                    value={getFieldVal('appointedBoardMembersPrice')}
                                    placeholder="0"
                                    label="Członek zarządu (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('appointedBoardMembersPrice')}
                                    helperText={getHelper('appointedBoardMembersPrice')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    name="premiumPersonnelPrice"
                                    value={getFieldVal('premiumPersonnelPrice')}
                                    placeholder="0"
                                    label="Kadry premium (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('premiumPersonnelPrice')}
                                    helperText={getHelper('premiumPersonnelPrice')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Grupa 3: Usługi podatkowe i deklaracje */}
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#F8FAFC'
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkTheme ? '#FDE047' : '#B45309', mb: 1.5 }}>
                            <i className="fas fa-coins" style={{ marginRight: 8 }}></i>
                            Deklaracje, podatki i usługi dodatkowe
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    name="additionalDeclaration"
                                    value={getFieldVal('additionalDeclaration')}
                                    placeholder="0"
                                    label="Dodatkowa deklaracja (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('additionalDeclaration')}
                                    helperText={getHelper('additionalDeclaration')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    name="correctionOfDeclaration"
                                    value={getFieldVal('correctionOfDeclaration')}
                                    placeholder="0"
                                    label="Korekta deklaracji z winy klienta (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('correctionOfDeclaration')}
                                    helperText={getHelper('correctionOfDeclaration')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    name="balanceSheetOnRequest"
                                    value={getFieldVal('balanceSheetOnRequest')}
                                    placeholder="0"
                                    label="Bilans i RZiS na żądanie (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('balanceSheetOnRequest')}
                                    helperText={getHelper('balanceSheetOnRequest')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    name="estonianCit"
                                    value={getFieldVal('estonianCit')}
                                    placeholder="0"
                                    label="Opłata za CIT estoński (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('estonianCit')}
                                    helperText={getHelper('estonianCit')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    name="additionalConsultation"
                                    value={getFieldVal('additionalConsultation')}
                                    placeholder="0"
                                    label="Dodatkowe konsultacje (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('additionalConsultation')}
                                    helperText={getHelper('additionalConsultation')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    name="otherServices"
                                    value={getFieldVal('otherServices')}
                                    placeholder="0"
                                    label="Inne usługi (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('otherServices')}
                                    helperText={getHelper('otherServices')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Grupa 4: Sprawozdawczość finansowa & KRS */}
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.3)' : '#F8FAFC'
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkTheme ? '#C084FC' : '#7E22CE', mb: 1.5 }}>
                            <i className="fas fa-chart-line" style={{ marginRight: 8 }}></i>
                            Sprawozdawczość finansowa & KRS
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    name="finReportSmallClient"
                                    value={getFieldVal('finReportSmallClient')}
                                    placeholder="0"
                                    label="SF mały klient (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('finReportSmallClient')}
                                    helperText={getHelper('finReportSmallClient')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    name="finReportMediumClient"
                                    value={getFieldVal('finReportMediumClient')}
                                    placeholder="0"
                                    label="SF średni klient (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('finReportMediumClient')}
                                    helperText={getHelper('finReportMediumClient')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    name="finReportLargeClient"
                                    value={getFieldVal('finReportLargeClient')}
                                    placeholder="0"
                                    label="SF duży klient (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('finReportLargeClient')}
                                    helperText={getHelper('finReportLargeClient')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    name="sfToKrsAssistance"
                                    value={getFieldVal('sfToKrsAssistance')}
                                    placeholder="0"
                                    label="Pomoc SF do eKRS (zł)"
                                    variant="outlined"
                                    size="small"
                                    onChange={onChangeHandler}
                                    fullWidth
                                    error={getFieldError('sfToKrsAssistance')}
                                    helperText={getHelper('sfToKrsAssistance')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        );
    };

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: darkTheme ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>
                    Cenniki Klientów
                </Typography>
                <Typography variant="body2" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                    Konfiguruj taryfy i pakiety rozliczeniowe dla klientów biura, limity dokumentów oraz stawki dodatkowe.
                </Typography>
            </Box>

            {/* Segmented Sub-Navigation */}
            <Box
                sx={{
                    display: 'inline-flex',
                    flexWrap: 'wrap',
                    p: '4px',
                    borderRadius: '10px',
                    backgroundColor: darkTheme ? 'rgba(255, 255, 255, 0.05)' : '#F1F5F9',
                    mb: 3,
                    border: '1px solid',
                    borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                    gap: 0.5
                }}
            >
                <Button
                    onClick={() => handleSwitchMode('edit')}
                    sx={{
                        px: 2.5,
                        py: 0.8,
                        borderRadius: '7px',
                        textTransform: 'none',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        color: mode === 'edit'
                            ? '#FFFFFF'
                            : (darkTheme ? '#94A3B8' : '#64748B'),
                        backgroundColor: mode === 'edit'
                            ? (darkTheme ? '#2563EB' : '#1D4ED8')
                            : 'transparent',
                        boxShadow: mode === 'edit' ? '0 2px 6px rgba(37, 99, 235, 0.3)' : 'none',
                        '&:hover': {
                            backgroundColor: mode === 'edit'
                                ? (darkTheme ? '#1D4ED8' : '#1E40AF')
                                : (darkTheme ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')
                        }
                    }}
                >
                    <i className="fas fa-edit" style={{ marginRight: 8 }}></i>
                    Edytuj istniejący cennik
                </Button>
                <Button
                    onClick={() => handleSwitchMode('add')}
                    sx={{
                        px: 2.5,
                        py: 0.8,
                        borderRadius: '7px',
                        textTransform: 'none',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        color: mode === 'add'
                            ? '#FFFFFF'
                            : (darkTheme ? '#94A3B8' : '#64748B'),
                        backgroundColor: mode === 'add'
                            ? (darkTheme ? '#10B981' : '#059669')
                            : 'transparent',
                        boxShadow: mode === 'add' ? '0 2px 6px rgba(16, 185, 129, 0.3)' : 'none',
                        '&:hover': {
                            backgroundColor: mode === 'add'
                                ? (darkTheme ? '#059669' : '#047857')
                                : (darkTheme ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')
                        }
                    }}
                >
                    <i className="fas fa-plus" style={{ marginRight: 8 }}></i>
                    Dodaj nowy cennik
                </Button>
                {canDeleteTariff && (
                    <Button
                        onClick={() => handleSwitchMode('delete')}
                        sx={{
                            px: 2.5,
                            py: 0.8,
                            borderRadius: '7px',
                            textTransform: 'none',
                            fontSize: '13.5px',
                            fontWeight: 600,
                            color: mode === 'delete'
                                ? '#FFFFFF'
                                : (darkTheme ? '#94A3B8' : '#64748B'),
                            backgroundColor: mode === 'delete'
                                ? (darkTheme ? '#EF4444' : '#DC2626')
                                : 'transparent',
                            boxShadow: mode === 'delete' ? '0 2px 6px rgba(239, 68, 68, 0.3)' : 'none',
                            '&:hover': {
                                backgroundColor: mode === 'delete'
                                    ? (darkTheme ? '#DC2626' : '#B91C1C')
                                    : (darkTheme ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')
                            }
                        }}
                    >
                        <i className="fas fa-trash-alt" style={{ marginRight: 8 }}></i>
                        Usuń cennik
                    </Button>
                )}
            </Box>

            {/* WIDOK: Edycja cennika */}
            {mode === 'edit' && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.2)' : '#FFFFFF',
                        mb: 3
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Grid container spacing={2.5} sx={{ mb: 3 }} alignItems="flex-start">
                            <Grid item xs={12} sm={6}>
                                <Controls.Select
                                    name="tariffId"
                                    label="Wybierz cennik"
                                    value={selectedTariff}
                                    onChange={handleClientTariffChange}
                                    options={tariffs}
                                    error={errors.tariffId}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Controls.Select
                                    name="packageId"
                                    label="Wybierz pakiet w cenniku"
                                    value={selectedPackage}
                                    onChange={handleClientPackageChange}
                                    options={packages}
                                    disabled={!selectedTariff}
                                    error={errors.packageId}
                                    size="small"
                                />
                            </Grid>
                        </Grid>

                        {selectedPackage && formData.description !== undefined && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                {renderPackageSectionCards({
                                    isEditMode: true,
                                    data: formData,
                                    onChangeHandler: (e) => localOnChange(e, true)
                                })}
                                <Box sx={{ mt: 3, textAlign: 'right' }}>
                                    <Controls.PremiumButton
                                        variant="green"
                                        label="Zapisz zmiany w pakiecie"
                                        icon="fas fa-save"
                                        onClick={handlePackageAmountsChange}
                                    />
                                </Box>
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* WIDOK: Dodawanie cennika */}
            {mode === 'add' && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        borderColor: darkTheme ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
                        backgroundColor: darkTheme ? 'rgba(30, 41, 59, 0.2)' : '#FFFFFF',
                        mb: 3
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: darkTheme ? '#F1F5F9' : '#0F172A', mb: 2 }}>
                            Konfiguracja nowej taryfy
                        </Typography>

                        <Grid container spacing={2.5} sx={{ mb: 3 }} alignItems="flex-start">
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="description"
                                    label="Nazwa nowego cennika"
                                    placeholder="np. Cennik 2026 Główny"
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={clientTariffDTO.description}
                                    onChange={handleDescriptionChange}
                                    fullWidth
                                    error={!!errors.clientTariffDTO?.description}
                                    helperText={errors.clientTariffDTO?.description}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Controls.Select
                                    name="numberOfPackages"
                                    label="Liczba pakietów do zdefiniowania"
                                    value={numberOfPackages || ''}
                                    onChange={handleNumberOfPackages}
                                    options={MyCollections.getPackageCollection()}
                                    error={errors.numberOfPackages}
                                    size="small"
                                />
                            </Grid>
                        </Grid>

                        {numberOfPackages > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {Array.from({ length: numberOfPackages }, (_, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: darkTheme ? 'rgba(255, 255, 255, 0.1)' : '#CBD5E1',
                                            backgroundColor: darkTheme ? 'rgba(15, 23, 42, 0.4)' : '#F8FAFC'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                                            <Chip
                                                label={`Pakiet ${index + 1}`}
                                                color="primary"
                                                size="small"
                                                sx={{ fontWeight: 600 }}
                                            />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                Konfiguracja stawek i limitów pakietu #{index + 1}
                                            </Typography>
                                        </Box>
                                        {renderPackageSectionCards({
                                            isEditMode: false,
                                            packageIndex: index,
                                            data: {},
                                            onChangeHandler: (e) => handlePackageFieldChange(e, index)
                                        })}
                                    </Box>
                                ))}

                                <Box sx={{ mt: 2, textAlign: 'right' }}>
                                    <Controls.PremiumButton
                                        variant="green"
                                        label="Utwórz i zapisz cały cennik"
                                        icon="fas fa-plus-circle"
                                        onClick={handleAddNewClientTariff}
                                    />
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* WIDOK: Usuwanie cennika */}
            {mode === 'delete' && canDeleteTariff && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        borderColor: darkTheme ? 'rgba(239, 68, 68, 0.2)' : '#FCA5A5',
                        backgroundColor: darkTheme ? 'rgba(239, 68, 68, 0.05)' : '#FEF2F2',
                        maxWidth: 720
                    }}
                >
                    <CardContent sx={{ p: 3.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#DC2626', mb: 1 }}>
                            Usuwanie istniejącego cennika
                        </Typography>
                        <Typography variant="body2" sx={{ color: darkTheme ? '#CBD5E1' : '#64748B', mb: 3 }}>
                            Usunięcie cennika wycofuje go z listy dostępnych taryf dla nowych klientów. Wcześniejsze przypisania do istniejących klientów pozostaną nienaruszone.
                        </Typography>

                        <Grid container spacing={2.5} alignItems="flex-start">
                            <Grid item xs={12} sm={8}>
                                <Controls.Select
                                    name="tariffId"
                                    label="Wybierz cennik do usunięcia"
                                    value={selectedTariff}
                                    onChange={handleClientTariffChange}
                                    options={tariffs}
                                    error={errors.tariffId}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    disabled={!selectedTariff}
                                    onClick={() => setOpenDeleteDialog(true)}
                                    sx={{
                                        height: 40,
                                        width: '100%',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)'
                                    }}
                                >
                                    <i className="fas fa-trash-alt" style={{ marginRight: 8 }}></i>
                                    Usuń cennik
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Dialog potwierdzenia usunięcia */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 2.5,
                        p: 1,
                        backgroundColor: darkTheme ? '#1E293B' : '#FFFFFF',
                        color: darkTheme ? '#F8FAFC' : '#0F172A'
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#EF4444' }}>
                    Potwierdzenie usunięcia cennika
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: darkTheme ? '#CBD5E1' : '#475569' }}>
                        Czy na pewno chcesz usunąć wybrany cennik? Cennik zostanie wycofany i przestanie być dostępny do wyboru dla nowych klientów. Historyczne dane przypisanych wcześniej klientów zostaną zachowane.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}
                    >
                        Anuluj
                    </Button>
                    <Button
                        onClick={handleDeleteClientTariff}
                        color="error"
                        variant="contained"
                        autoFocus
                        sx={{ borderRadius: 1.5 }}
                    >
                        Tak, usuń cennik
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
